// DTOs/schemas do módulo "contas" (cadastro, sessão e recuperação de senha).
import { z } from "zod";

import type { Role, StatusConta } from "./enums";

// ───────────────────────── Documento (CPF/CNPJ) ─────────────────────────

/** Remove tudo que não é dígito. */
export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, "");
}

function todosIguais(digitos: string): boolean {
  return /^(\d)\1+$/.test(digitos);
}

/** Valida CPF (11 dígitos + dois dígitos verificadores). Aceita máscara. */
export function validarCpf(valor: string): boolean {
  const d = somenteDigitos(valor);
  if (d.length !== 11 || todosIguais(d)) return false;
  const dv = (tamanho: number): number => {
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(d[i]) * (tamanho + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
}

/** Valida CNPJ (14 dígitos + dois dígitos verificadores). Aceita máscara. */
export function validarCnpj(valor: string): boolean {
  const d = somenteDigitos(valor);
  if (d.length !== 14 || todosIguais(d)) return false;
  const dv = (tamanho: number): number => {
    const pesos = tamanho === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(d[i]) * (pesos[i] as number);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return dv(12) === Number(d[12]) && dv(13) === Number(d[13]);
}

/** CPF (11) ou CNPJ (14) válido. */
export function validarDocumento(valor: string): boolean {
  const d = somenteDigitos(valor);
  return d.length === 11 ? validarCpf(d) : d.length === 14 ? validarCnpj(d) : false;
}

// ───────────────────────── Schemas de entrada ─────────────────────────

/** bcrypt só considera os 72 primeiros BYTES da senha; acima disso recusamos. */
export const SENHA_MIN = 8;
export const SENHA_MAX_BYTES = 72;

const zEmail = z
  .string({ required_error: "Informe o e-mail." })
  .trim()
  .toLowerCase()
  .email("E-mail inválido.")
  .max(200, "E-mail muito longo.");

export const zSenha = z
  .string({ required_error: "Informe a senha." })
  .min(SENHA_MIN, `A senha deve ter no mínimo ${SENHA_MIN} caracteres.`)
  .refine((s) => new TextEncoder().encode(s).length <= SENHA_MAX_BYTES, "A senha é muito longa.");

function zNomeEmpresa() {
  return z
    .string({ required_error: "Informe o nome da empresa." })
    .trim()
    .min(2, "Informe o nome da empresa.")
    .max(120, "Nome da empresa muito longo.");
}

/** CPF ou CNPJ, com ou sem máscara; a API guarda só os dígitos. */
const zDocumentoCadastro = z
  .string({ required_error: "Informe o CPF ou CNPJ." })
  .transform(somenteDigitos)
  .refine(validarDocumento, "CPF ou CNPJ inválido.");

/** Telefone com DDD (10 a 13 dígitos, DDI opcional). */
const zTelefoneCadastro = z
  .string({ required_error: "Informe o telefone." })
  .transform(somenteDigitos)
  .refine((d) => d.length >= 10 && d.length <= 13, "Telefone inválido. Informe DDD e número.");

/** Código de 6 dígitos enviado ao e-mail por `POST /auth/cadastro/codigo`. */
export const zCodigo = z
  .string({ required_error: "Informe o código enviado ao seu e-mail." })
  .trim()
  .regex(/^\d{6}$/, "O código tem 6 dígitos.");

/** Telefone BR com DDD → `55DDDNNNNNNNNN` (comparação e unicidade). Aceita máscara e DDI. */
export function normalizarCelular(valor: string): string {
  const d = somenteDigitos(valor);
  return d.startsWith("55") && d.length >= 12 ? d : `55${d}`;
}

export const cadastroSchema = z.object({
  nome: z
    .string({ required_error: "Informe seu nome." })
    .trim()
    .min(2, "Informe seu nome.")
    .max(120, "Nome muito longo."),
  email: zEmail,
  senha: zSenha,
  codigo: zCodigo,
  nomeEmpresa: zNomeEmpresa(),
  documento: zDocumentoCadastro,
  telefone: zTelefoneCadastro,
  /** publicId do plano vindo de `?plano=`. Plano inexistente/inativo é ignorado, não é erro. */
  planoId: z.string().trim().max(64).optional(),
});
export type CadastroDto = z.infer<typeof cadastroSchema>;

/** `POST /auth/cadastro/codigo`. */
export const pedirCodigoSchema = z.object({ email: zEmail });
export type PedirCodigoDto = z.infer<typeof pedirCodigoSchema>;

/** Resposta única de `POST /auth/cadastro/codigo`, exista o e-mail ou não. */
export const MENSAGEM_CODIGO_ENVIADO =
  "Se o e-mail puder ser usado, enviamos um código de 6 dígitos. Ele vale por 10 minutos.";

/** `POST /auth/google`: `credential` é o ID token do Google Identity Services. */
export const googleLoginSchema = z.object({
  credential: z.string({ required_error: "Token do Google ausente." }).min(20).max(4096),
});
export type GoogleLoginDto = z.infer<typeof googleLoginSchema>;

/** `POST /auth/google/cadastro`: completa o cadastro de quem veio do Google (sem senha nem código). */
export const googleCadastroSchema = z.object({
  tokenCadastro: z.string({ required_error: "Sessão expirada." }).min(20).max(4096),
  nomeEmpresa: zNomeEmpresa(),
  documento: zDocumentoCadastro,
  telefone: zTelefoneCadastro,
  planoId: z.string().trim().max(64).optional(),
});
export type GoogleCadastroDto = z.infer<typeof googleCadastroSchema>;

/** Resposta de `POST /auth/google` quando o e-mail ainda não tem conta. */
export interface GoogleCadastroPendente {
  status: "cadastro_pendente";
  tokenCadastro: string;
  email: string;
  nome: string;
}

export const loginSchema = z.object({
  /** E-mail ou celular. Com "@" é e-mail; senão, celular. */
  login: z
    .string({ required_error: "Informe seu e-mail ou celular." })
    .trim()
    .toLowerCase()
    .min(3, "Informe seu e-mail ou celular.")
    .max(200),
  // Sem regra de tamanho no login: não revela a política e não bloqueia senhas antigas.
  senha: z.string({ required_error: "Informe a senha." }).min(1, "Informe a senha.").max(256),
});
export type LoginDto = z.infer<typeof loginSchema>;

export const esqueciSenhaSchema = z.object({ email: zEmail });
export type EsqueciSenhaDto = z.infer<typeof esqueciSenhaSchema>;

export const redefinirSenhaSchema = z.object({
  token: z.string({ required_error: "Link inválido." }).trim().min(20, "Link inválido.").max(200),
  novaSenha: zSenha,
});
export type RedefinirSenhaDto = z.infer<typeof redefinirSenhaSchema>;

export const alterarSenhaSchema = z.object({
  senhaAtual: z.string({ required_error: "Informe a senha atual." }).min(1, "Informe a senha atual.").max(256),
  novaSenha: zSenha,
});
export type AlterarSenhaDto = z.infer<typeof alterarSenhaSchema>;

// ───────────────────────── Respostas ─────────────────────────

/** Mensagem única de `POST /auth/esqueci-senha`, exista o e-mail ou não. */
export const MENSAGEM_ESQUECI_SENHA =
  "Se o e-mail estiver cadastrado, enviaremos um link para redefinir a senha.";

/** Mensagem única de cadastro recusado, exista o e-mail ou não. */
export const MENSAGEM_CADASTRO_RECUSADO = "Não foi possível concluir o cadastro.";

export interface UsuarioSessao {
  id: string; // Usuario.publicId
  nome: string;
  email: string;
  role: Role;
  /** true → o front deve levar o usuário a trocar a senha antes de continuar. */
  deveTrocarSenha: boolean;
}

/** Resposta de login/cadastro/refresh/alterar-senha. O refresh token vai no cookie, nunca no corpo. */
export interface SessaoResposta {
  accessToken: string;
  /** Validade do access token, em segundos. */
  expiresIn: number;
  usuario: UsuarioSessao;
}

export interface PlanoDoContexto {
  id: string; // Plano.publicId
  nome: string;
  precoCentavos: number;
  limiteNumeros: number;
}

/** Resposta de `GET /auth/eu`. Para SUPERADMIN, `conta`, `plano` e `numeros` são null. */
export interface ContextoEuResposta {
  usuario: UsuarioSessao;
  conta: {
    id: string; // Conta.publicId
    nome: string;
    status: StatusConta;
    trialEndsAt: string | null;
  } | null;
  plano: PlanoDoContexto | null;
  numeros: { usados: number; limite: number } | null;
  /** false quando a conta está suspensa/cancelada ou o trial acabou. */
  assinaturaAtiva: boolean;
  /** publicId do SUPERADMIN quando é impersonação. */
  impersonadoPor: string | null;
}

export interface MensagemResposta {
  mensagem: string;
}
